/**
 * Billing data layer (Phase 4A-2) — TanStack Query hooks over the /billing API.
 * Mirrors auth/hooks.ts: ONE cached status query the whole app reads, so the
 * guard, the nav, and the billing page all agree without re-fetching.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, SubscriptionStatus } from '@/lib/api';

export const billingKeys = {
  status: ['billing', 'status'] as const,
};

/** null = not signed in (401). Any other error is real and should surface. */
async function fetchStatus(): Promise<SubscriptionStatus | null> {
  try {
    return await api.subscriptionStatus();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function useSubscription() {
  return useQuery({ queryKey: billingKeys.status, queryFn: fetchStatus, retry: false });
}

/** Start hosted Stripe Checkout — the caller redirects to the returned URL. */
export function useStartCheckout() {
  return useMutation({ mutationFn: api.createCheckout });
}

/** Open the hosted Stripe Billing Portal — the caller redirects to the URL. */
export function useOpenPortal() {
  return useMutation({ mutationFn: api.billingPortal });
}
